import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { StatusBadge, TypeBadge } from "@/components/StatusBadge";
import { Progress } from "@/components/ui/progress";
import { CalendarDays, Plane, Stethoscope, Clock, ArrowUpRight } from "lucide-react";
import { format, parseISO } from "date-fns";
import { useNavigate } from "react-router-dom";

const fmt = (d) => format(parseISO(d), "d MMM yyyy");

function Metric({ icon: Icon, label, value, sub, color }) {
  return (
    <div className="bg-white border rounded-lg p-5" style={{ borderColor: "#EAE8E1" }} data-testid={`metric-${label.toLowerCase().replace(/\s/g, "-")}`}>
      <div className="flex items-center justify-between">
        <span className="text-xs uppercase tracking-[0.15em] font-semibold" style={{ color: "#6A736D" }}>
          {label}
        </span>
        <span className="grid place-items-center w-8 h-8 rounded-md" style={{ backgroundColor: color + "1a" }}>
          <Icon className="w-4 h-4" style={{ color }} strokeWidth={1.75} />
        </span>
      </div>
      <p className="font-heading text-3xl font-medium mt-3" style={{ color: "#2C332E" }}>{value}</p>
      {sub && <p className="text-sm mt-1" style={{ color: "#6A736D" }}>{sub}</p>}
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/dashboard").then((r) => setData(r.data));
  }, []);

  if (!data) return <div className="text-sm" style={{ color: "#6A736D" }}>Loading…</div>;
  const b = data.balance;
  const pct = b.allowance ? Math.min(100, (b.used / b.allowance) * 100) : 0;

  return (
    <div className="space-y-8">
      <div>
        <h1 className="font-heading text-3xl font-medium tracking-tight">
          Hello, {user?.name?.split(" ")[0]} 👋
        </h1>
        <p className="text-sm mt-1" style={{ color: "#6A736D" }}>
          Here's your leave overview for the year.
        </p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Metric icon={Plane} label="Remaining" value={b.remaining} sub={`of ${b.allowance} days`} color="#647D6C" />
        <Metric icon={CalendarDays} label="Taken" value={b.used} sub="approved holiday" color="#5C7E9E" />
        <Metric icon={Clock} label="Pending" value={b.pending} sub="awaiting approval" color="#D48B71" />
        <Metric icon={Stethoscope} label="Sick Days" value={b.sick_days} sub="logged this year" color="#948A7B" />
      </div>

      <div className="bg-white border rounded-lg p-5" style={{ borderColor: "#EAE8E1" }}>
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-heading text-lg font-medium">Annual allowance</h3>
          <span className="text-sm" style={{ color: "#6A736D" }}>{b.used} / {b.allowance} used</span>
        </div>
        <Progress value={pct} className="h-2.5" />
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="bg-white border rounded-lg p-5" style={{ borderColor: "#EAE8E1" }}>
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-heading text-lg font-medium">Upcoming leave</h3>
            <button
              data-testid="goto-calendar-button"
              onClick={() => navigate("/calendar")}
              className="text-sm flex items-center gap-1 hover:underline"
              style={{ color: "#647D6C" }}
            >
              Calendar <ArrowUpRight className="w-3.5 h-3.5" />
            </button>
          </div>
          {data.upcoming.length === 0 ? (
            <p className="text-sm" style={{ color: "#6A736D" }}>No upcoming approved leave.</p>
          ) : (
            <ul className="space-y-3">
              {data.upcoming.map((r) => (
                <li key={r.id} className="flex items-center justify-between" data-testid={`upcoming-${r.id}`}>
                  <div>
                    <div className="flex items-center gap-2">
                      <TypeBadge type={r.type} />
                      <span className="text-sm font-medium">{fmt(r.start_date)}{r.start_date !== r.end_date ? ` – ${fmt(r.end_date)}` : ""}</span>
                    </div>
                    {r.note && <p className="text-xs mt-1" style={{ color: "#6A736D" }}>{r.note}</p>}
                  </div>
                  <span className="text-sm font-medium" style={{ color: "#2C332E" }}>{r.days}d</span>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="bg-white border rounded-lg p-5" style={{ borderColor: "#EAE8E1" }}>
          <h3 className="font-heading text-lg font-medium mb-4">Recent activity</h3>
          {data.recent.length === 0 ? (
            <p className="text-sm" style={{ color: "#6A736D" }}>No recent requests.</p>
          ) : (
            <ul className="space-y-3">
              {data.recent.map((r) => (
                <li key={r.id} className="flex items-center justify-between" data-testid={`recent-${r.id}`}>
                  <div className="flex items-center gap-2">
                    <TypeBadge type={r.type} />
                    <span className="text-sm">{fmt(r.start_date)}</span>
                  </div>
                  <StatusBadge status={r.status} />
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
